/**
 * branding-preview — the Brand Kit's customer-page preview.
 *
 * What it is: a BRAND SPECIMEN in the shape of each published journey — the
 * page chrome (header, hero, step rail, buttons, footer) drawn with the kit's
 * live colours, fonts, radius and logo, so an author can see what a colour
 * choice does to a booking page without leaving Settings.
 *
 * What it is NOT: the customer's page. It renders NO services, locations,
 * events, times or prices — the grey bars stand for content that comes from the
 * journey's own setup in All Journeys, and the caption on the frame says so.
 * That is deliberate: inventing plausible tenant content here would be exactly
 * the fabricated-data class the no-seed-data rule exists to stop, and a preview
 * that quietly disagreed with the real page would be worse than no preview.
 *
 * Data: unbound by design — this component takes the in-memory kit draft as
 * props and issues no request of its own.
 */
import { useRef, useState, type CSSProperties, type ReactElement } from "react";
import { T } from "@v0y4ge/jrni-ui";
import { useT, t as i18nT } from "../../lib/i18n";
import type { Brand } from "./branding-kit";

/** The six journeys All Journeys publishes, in its own tab order. */
export type PreviewJourney = "appointment" | "events" | "landing" | "queue" | "kiosk" | "onestop";

export const PREVIEW_JOURNEYS: { id: PreviewJourney; key: string; label: string }[] = [
  { id: "appointment", key: "settings.brandKit.journeyAppointments", get label() { return i18nT("settings.brandKit.journeyAppointments", "Appointments"); } },
  { id: "events", key: "settings.brandKit.journeyEventList", get label() { return i18nT("settings.brandKit.journeyEventList", "Event list"); } },
  { id: "landing", key: "settings.brandKit.journeyEventLanding", get label() { return i18nT("settings.brandKit.journeyEventLanding", "Event landing"); } },
  { id: "queue", key: "settings.brandKit.journeyWalkIns", get label() { return i18nT("settings.brandKit.journeyWalkIns", "Walk-ins"); } },
  { id: "kiosk", key: "settings.brandKit.journeyKiosk", get label() { return i18nT("settings.brandKit.journeyKiosk", "Kiosk"); } },
  { id: "onestop", key: "settings.brandKit.journeyOneStop", get label() { return i18nT("settings.brandKit.journeyOneStop", "One Stop"); } },
];

/** A grey bar standing in for journey content. Decorative — the caption on the
 *  frame carries the meaning, so these stay out of the a11y tree. */
function Bar({ w = "100%", h = 10, mt = 0 }: { w?: number | string; h?: number; mt?: number }) {
  return <span aria-hidden="true" style={{ display: "block", width: w, height: h, marginTop: mt, borderRadius: 999, background: "rgba(20,19,31,0.10)" }} />;
}
function Tile({ h = 64, radius = 10 }: { h?: number; radius?: number }) {
  return <span aria-hidden="true" style={{ display: "block", height: h, borderRadius: radius, background: "rgba(20,19,31,0.07)", border: "1px dashed rgba(20,19,31,0.14)" }} />;
}

/**
 * HtmlSlot — renders an author's own header/footer markup.
 *
 * It goes in an iframe with `sandbox="allow-same-origin"` and NO
 * `allow-scripts`: a pasted `<script>` or inline `onclick` cannot run, and the
 * author's CSS cannot leak out and restyle Studio around it. Same-origin is
 * granted only so the parent can read `scrollHeight` and size the frame to the
 * content — with scripts blocked that is not an XSS surface.
 *
 * The live page will run this through the platform's sanitiser; the preview is
 * deliberately the stricter of the two, so anything that looks right here is
 * safe there.
 */
function HtmlSlot({ html, css, fontStack, label }: { html: string; css: string; fontStack: string; label: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(72);
  const doc = `<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;padding:0;}body{font-family:${fontStack};}${css}</style></head><body>${html}</body></html>`;
  const measure = () => {
    const d = ref.current?.contentDocument;
    if (d) setHeight(Math.max(24, Math.min(720, d.documentElement.scrollHeight)));
  };
  return (
    <iframe ref={ref} title={label} srcDoc={doc} sandbox="allow-same-origin" onLoad={measure}
      style={{ display: "block", width: "100%", height, border: "none", background: "#fff" }} />
  );
}

export function BrandPreview({ brand, assets, journey, tenantName, compact }: {
  brand: Brand;
  assets: Record<string, string | null>;
  journey: PreviewJourney;
  tenantName?: string | null;
  /** Mobile/tablet frame — tightens padding and drops to one column. */
  compact?: boolean;
}) {
  const t = useT();
  const radius = Number(brand.buttonRadius) || 0;
  const pad = compact ? 16 : 28;

  const heading: CSSProperties = { fontFamily: `${brand.headingFont}, ${brand.fontFallback}`, fontWeight: Number(brand.headingWeight), color: brand.neutralText, margin: 0 };
  const body: CSSProperties = { fontFamily: `${brand.bodyFont}, ${brand.fontFallback}`, fontWeight: Number(brand.bodyWeight), color: brand.neutralText };
  const primaryBtn: CSSProperties = {
    display: "inline-block", padding: compact ? "10px 16px" : "12px 22px", fontSize: 13.5, fontWeight: 600,
    fontFamily: `${brand.bodyFont}, ${brand.fontFallback}`, textAlign: "center",
    ...(brand.buttonStyle === "outline"
      ? { background: "transparent", color: brand.primary, border: `2px solid ${brand.primary}`, borderRadius: radius }
      : brand.buttonStyle === "pill"
        ? { background: brand.primary, color: "#fff", border: "none", borderRadius: 999 }
        : { background: brand.primary, color: "#fff", border: "none", borderRadius: radius }),
  };
  const secondaryBtn: CSSProperties = { ...primaryBtn, background: "transparent", color: brand.link, border: `1px solid ${brand.link}` };
  const card: CSSProperties = { background: "#fff", border: "1px solid rgba(20,19,31,0.10)", borderRadius: Math.max(8, radius), padding: compact ? 14 : 18, display: "flex", flexDirection: "column", gap: 10 };

  const wordmark = assets.logo || assets.logoIcon;
  const reverseMark = assets.logoReverse || assets.logo;
  const brandName = brand.name || tenantName || t("settings.brandKit.yourBrand", "Your brand");

  const Header = ({ onDark }: { onDark?: boolean }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: `14px ${pad}px`, background: onDark ? "transparent" : "#fff", borderBottom: onDark ? "none" : "1px solid rgba(20,19,31,0.08)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        {(onDark ? reverseMark : wordmark)
          ? <img src={(onDark ? reverseMark : wordmark) as string} alt="" style={{ height: 24, maxWidth: 140, objectFit: "contain" }} />
          : <span style={{ ...heading, fontSize: 15, color: onDark ? "#fff" : brand.primary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{brandName}</span>}
      </div>
      <Bar w={compact ? 48 : 72} h={8} />
    </div>
  );

  const Steps = ({ labels }: { labels: string[] }) => (
    <div style={{ display: "flex", alignItems: "center", gap: compact ? 6 : 10, flexWrap: "wrap" }}>
      {labels.map((s, i) => (
        <span key={s} style={{ display: "inline-flex", alignItems: "center", gap: compact ? 6 : 10 }}>
          <span style={{ ...body, fontSize: 11.5, fontWeight: i === 0 ? 700 : 500, color: i === 0 ? brand.primary : "rgba(20,19,31,0.45)", whiteSpace: "nowrap" }}>{s}</span>
          {i < labels.length - 1 ? <span aria-hidden="true" style={{ width: compact ? 10 : 18, height: 1, background: "rgba(20,19,31,0.18)" }} /> : null}
        </span>
      ))}
    </div>
  );

  const Footer = () => (
    <div style={{ padding: `16px ${pad}px 20px`, background: brand.cover, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      {reverseMark ? <img src={reverseMark as string} alt="" style={{ height: 20, objectFit: "contain", opacity: 0.95 }} /> : <span style={{ ...body, fontSize: 12, color: "rgba(255,255,255,0.92)" }}>{brandName}</span>}
      <span style={{ ...body, fontSize: 10.5, color: "rgba(255,255,255,0.75)", textAlign: "center" }}>
        {brand.footer}{brand.poweredBy ? t("settings.brandingPreview.text", "· Powered by JRNI") : ""}
      </span>
    </div>
  );

  const bodyPad: CSSProperties = { padding: `${compact ? 18 : 26}px ${pad}px ${compact ? 22 : 30}px`, display: "flex", flexDirection: "column", gap: compact ? 14 : 18 };

  let content: ReactElement;
  switch (journey) {
    case "appointment":
      content = (
        <div style={bodyPad}>
          <Steps labels={[
            t("settings.brandKit.stepService", "Service"),
            t("settings.brandKit.stepLocation", "Location"),
            t("settings.brandKit.stepDateTime", "Date & time"),
            t("settings.brandKit.stepDetails", "Your details"),
          ]} />
          <h1 style={{ ...heading, fontSize: compact ? 22 : 30 }}>{t("settings.brandKit.bookAnAppointment", "Book an appointment")}</h1>
          <div style={{ display: "grid", gap: 10 }}>
            {[0, 1, 2].map((i) => (
              <div key={i} style={{ ...card, flexDirection: "row", alignItems: "center", gap: 14 }}>
                <span aria-hidden="true" style={{ width: 40, height: 40, borderRadius: Math.max(6, radius - 2), background: i === 0 ? brand.accent : "rgba(20,19,31,0.07)", flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0 }}><Bar w="58%" /><Bar w="34%" h={8} mt={8} /></span>
                <span style={{ ...secondaryBtn, padding: "8px 14px", fontSize: 12 }}>{t("settings.brandKit.select", "Select")}</span>
              </div>
            ))}
          </div>
          <span style={primaryBtn}>{t("settings.brandKit.continue", "Continue")}</span>
        </div>
      );
      break;
    case "events":
      content = (
        <div style={bodyPad}>
          <h1 style={{ ...heading, fontSize: compact ? 22 : 30 }}>{t("settings.brandKit.whatSOn", "What's on")}</h1>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ flex: 1, height: 38, borderRadius: Math.max(6, radius), border: "1px solid rgba(20,19,31,0.14)", background: "#fff", display: "flex", alignItems: "center", padding: "0 12px" }}><Bar w="42%" h={8} /></span>
            <span style={{ ...primaryBtn, padding: "9px 16px", fontSize: 12.5 }}>{t("settings.brandKit.search", "Search")}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: compact ? "1fr" : "1fr 1fr", gap: 12 }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} style={card}>
                <Tile h={compact ? 72 : 86} radius={Math.max(6, radius - 2)} />
                <Bar w="72%" /><Bar w="46%" h={8} />
                <span style={{ ...primaryBtn, padding: "8px 14px", fontSize: 12, alignSelf: "flex-start" }}>{t("settings.brandKit.register", "Register")}</span>
              </div>
            ))}
          </div>
        </div>
      );
      break;
    case "landing":
      content = (
        <>
          <div style={{ background: brand.cover, padding: `${compact ? 26 : 44}px ${pad}px`, display: "flex", flexDirection: "column", gap: 12 }}>
            <span style={{ ...body, fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,0.7)" }}>{t("settings.brandKit.event", "Event")}</span>
            <h1 style={{ ...heading, fontSize: compact ? 24 : 38, color: "#fff" }}>{brand.tagline || t("settings.brandKit.eventTitleGoesHere", "The event title goes here")}</h1>
            <span style={{ display: "block", width: compact ? "80%" : "52%" }}><Bar w="100%" h={9} /><Bar w="70%" h={9} mt={8} /></span>
            <span style={{ ...primaryBtn, alignSelf: "flex-start", marginTop: 6 }}>{t("settings.brandKit.registerNow", "Register now")}</span>
          </div>
          <div style={{ ...bodyPad, display: "grid", gridTemplateColumns: compact ? "1fr" : "1.4fr 1fr", gap: 18 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <h2 style={{ ...heading, fontSize: compact ? 16 : 19 }}>{t("settings.brandKit.aboutThisEvent", "About this event")}</h2>
              <Bar /><Bar w="94%" /><Bar w="88%" /><Bar w="40%" />
            </div>
            <div style={card}>
              <Bar w="52%" h={9} /><Tile h={compact ? 56 : 72} radius={Math.max(6, radius - 2)} />
              <span style={primaryBtn}>{t("settings.brandKit.register", "Register")}</span>
            </div>
          </div>
        </>
      );
      break;
    case "queue":
      content = (
        <div style={bodyPad}>
          <Steps labels={[
            t("settings.brandKit.stepWhat", "What you need"),
            t("settings.brandKit.stepWhere", "Where"),
            t("settings.brandKit.stepDetails", "Your details"),
            t("settings.brandKit.stepTicket", "Your ticket"),
          ]} />
          <h1 style={{ ...heading, fontSize: compact ? 22 : 30 }}>{t("settings.brandKit.joinTheQueue", "Join the queue")}</h1>
          <div style={{ display: "grid", gridTemplateColumns: compact ? "1fr" : "1fr 1fr", gap: 10 }}>
            {[0, 1].map((i) => (
              <div key={i} style={{ ...card, borderColor: i === 0 ? brand.primary : "rgba(20,19,31,0.10)", borderWidth: i === 0 ? 2 : 1 }}>
                <Bar w="64%" /><Bar w="38%" h={8} />
              </div>
            ))}
          </div>
          <div style={{ ...card, background: brand.neutralBg, alignItems: "center", textAlign: "center", gap: 6 }}>
            <span style={{ ...body, fontSize: 11.5, color: "rgba(20,19,31,0.55)" }}>{t("settings.brandKit.yourPosition", "Your position")}</span>
            <span style={{ ...heading, fontSize: compact ? 30 : 40, color: brand.primary }}>—</span>
            <span style={{ ...body, fontSize: 11.5, color: "rgba(20,19,31,0.55)" }}>{t("settings.brandKit.positionArrivesFromTheQueue", "Filled in by the queue once you join")}</span>
          </div>
          <span style={primaryBtn}>{t("settings.brandKit.joinNow", "Join now")}</span>
        </div>
      );
      break;
    case "kiosk":
      content = (
        <div style={{ ...bodyPad, minHeight: compact ? 280 : 340, justifyContent: "center", alignItems: "center", textAlign: "center", background: brand.neutralBg, gap: 20 }}>
          <h1 style={{ ...heading, fontSize: compact ? 26 : 40 }}>{t("settings.brandKit.welcome", "Welcome")}</h1>
          <span style={{ ...body, fontSize: compact ? 14 : 17, color: "rgba(20,19,31,0.6)", maxWidth: 420 }}>{t("settings.brandKit.checkInOrJoinTheQueue", "Check in for an appointment, or join the queue.")}</span>
          <div style={{ display: "grid", gridTemplateColumns: compact ? "1fr" : "1fr 1fr", gap: 14, width: "100%", maxWidth: 480 }}>
            <span style={{ ...primaryBtn, padding: compact ? "18px 20px" : "26px 24px", fontSize: compact ? 15 : 17 }}>{t("settings.brandKit.iHaveAnAppointment", "I have an appointment")}</span>
            <span style={{ ...secondaryBtn, padding: compact ? "18px 20px" : "26px 24px", fontSize: compact ? 15 : 17 }}>{t("settings.brandKit.joinTheQueue", "Join the queue")}</span>
          </div>
        </div>
      );
      break;
    default:
      content = (
        <div style={bodyPad}>
          <h1 style={{ ...heading, fontSize: compact ? 22 : 30 }}>{t("settings.brandKit.howCanWeHelp", "How can we help?")}</h1>
          <div style={{ display: "grid", gridTemplateColumns: compact ? "1fr" : "1fr 1fr 1fr", gap: 12 }}>
            {[brand.primary, brand.accent, brand.link].map((c, i) => (
              <div key={i} style={{ ...card, gap: 12 }}>
                <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: Math.max(6, radius - 2), background: c }} />
                <Bar w="70%" /><Bar w="46%" h={8} />
                <span style={{ ...secondaryBtn, padding: "8px 14px", fontSize: 12, alignSelf: "flex-start" }}>{t("settings.brandKit.open", "Open")}</span>
              </div>
            ))}
          </div>
          <div style={{ ...card, background: brand.neutralBg }}>
            <Bar w="40%" h={9} /><Bar w="86%" /><Bar w="62%" />
          </div>
        </div>
      );
  }

  const onDarkHeader = journey === "landing";
  const customHeader = brand.headerMode === "custom" && !!brand.headerHtml.trim();
  const customFooter = brand.footerMode === "custom" && !!brand.footerHtml.trim();
  const fontStack = `${brand.bodyFont}, ${brand.fontFallback}`;
  return (
    <div style={{ background: onDarkHeader && !customHeader ? brand.cover : brand.neutralBg, minHeight: "100%", display: "flex", flexDirection: "column" }}>
      {customHeader
        ? <HtmlSlot html={brand.headerHtml} css={brand.customCss} fontStack={fontStack} label={t("settings.brandKit.customHeaderPreview", "Custom header")} />
        : <Header onDark={onDarkHeader} />}
      <div style={{ flex: 1 }}>{content}</div>
      {customFooter
        ? <HtmlSlot html={brand.footerHtml} css={brand.customCss} fontStack={fontStack} label={t("settings.brandKit.customFooterPreview", "Custom footer")} />
        : <Footer />}
    </div>
  );
}
